import { Flex, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useSetDefaultCafeteriaMutation } from '../../api/selectCafeteria';
import { ActionPopUpModal } from '@/components/ActionPopUpModal';
import { Button } from '@/components/Elements';
import { SelectField } from '@/components/Form';
import { CheckboxFormField } from '@/components/Form/CheckboxField';
import { ASSETS_BUCKET_URL } from '@/config';
import { queryClient } from '@/lib/react-query';
import { useCartDetails } from '@/modules/cart/api/cartDetails';
import { useDeleteCart } from '@/modules/cart/api/deleteCart';
import { removeCartStorage } from '@/utils/cart';
import storage from '@/utils/storage';

type SelectCafeteriaValues = {
  city: string;
  cafeteria: string;
  defaultCafeteria?: boolean;
};

type SelectCafeteriaFormProps = {
  onSuccess: (path?: string) => void;
  redirectCart: boolean;
};

export const SelectCafeteriaForm = ({ onSuccess, redirectCart }: SelectCafeteriaFormProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)', true);
  const locationMap = storage.getItem('locationMap');
  const currentLocation = storage.getItem('currentLocation');
  const webAccessToken = storage.getItem('webAccessToken');
  const config = storage.getItem('config');
  const groupLocation = config?.group_location;
  const cafeteriaSelected = Boolean(sessionStorage.getItem('cafeteriaSelected')) || false;
  const form = useForm<SelectCafeteriaValues>({
    initialValues: webAccessToken
      ? {
          city: currentLocation?.city_id,
          cafeteria: currentLocation?.id,
          defaultCafeteria: false,
        }
      : {
          city: cafeteriaSelected ? currentLocation?.city_id : '',
          cafeteria: cafeteriaSelected ? currentLocation?.id : '',
          defaultCafeteria: false,
        },
    validate: groupLocation
      ? {
          city: (value) => (value.length === 0 ? 'Please select your city' : null),
          cafeteria: (value) => (value.length === 0 ? 'Please select your cafeteria' : null),
        }
      : {
          cafeteria: (value) => (value.length === 0 ? 'Please select your cafeteria' : null),
        },
  });

  const deleteCartMutation = useDeleteCart();
  const { data: cartData } = useCartDetails({
    home_screen: true,
  });
  const getAllCafeterias = useCallback(() => {
    const data =
      locationMap &&
      Object.keys(locationMap).map((key) => ({
        label: locationMap[key].name,
        value: locationMap[key].id,
      }));
    return data;
  }, [locationMap]);

  const [cities, setCities] = useState<any>([]);
  const [cafeteria, setCafeteria] = useState<any>(groupLocation ? [] : getAllCafeterias());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getCities = useCallback(() => {
    const cityNames =
      locationMap &&
      Object.keys(locationMap)
        .map((key) => ({
          label: locationMap[key].city_name,
          value: locationMap[key].city_id,
        }))
        .filter((v, i, a) => a.findIndex((t) => t.label === v.label) === i);

    return cityNames;
  }, [locationMap]);

  const getCafeteria = useCallback(
    (cityId: string) => {
      const filterData =
        locationMap &&
        Object.keys(locationMap)
          .filter((key) => locationMap[key].city_id === cityId)
          .map((key) => ({
            label: locationMap[key].name,
            value: locationMap[key].id,
          }));
      return filterData;
    },
    [locationMap]
  );

  useEffect(() => {
    const cityNames = getCities();
    setCities(cityNames);
    if (!groupLocation) {
      const data = getAllCafeterias();
      setCafeteria(data);
    }
  }, []);

  useEffect(() => {
    if (groupLocation) {
      if (form.values.city !== currentLocation?.city_id) {
        form.setFieldValue('cafeteria', '');
      }
      if (form.values.city) {
        const data = getCafeteria(form.values.city);
        setCafeteria(data);
      }
    }
  }, [form.values.city]);

  const { mutateAsync } = useSetDefaultCafeteriaMutation();

  const updateData = async (cafeteriaId: string) => {
    sessionStorage.setItem('cafeteriaSelected', '1');
    sessionStorage.removeItem('currentOccasion');
    storage.removeItem('currentOccasion');
    storage.removeItem('occasions');
    removeCartStorage();
    const cafeteria = locationMap[cafeteriaId];
    storage.setItem('currentLocation', cafeteria);
    sessionStorage.setItem('currentLocation', JSON.stringify(cafeteria));
    return true;
  };

  const clearCart = (values: SelectCafeteriaValues) => {
    if (cartData?.cart_data) {
      void deleteCartMutation
        .mutateAsync({
          cart_ref: cartData.cart_data.cart_ref,
          version: cartData.cart_data.version,
        })
        .then(async () => {
          void handleCityChange(values);
        })
        .catch(() => {});
    } else {
      onSuccess();
    }
  };

  const handleCityChange = async (values: SelectCafeteriaValues) => {
    const cafeteriaId = values.cafeteria;
    if (values.defaultCafeteria) {
      try {
        const res = await mutateAsync({ location_id: cafeteriaId });
        if (res) {
          const refreshData = await updateData(cafeteriaId);
          void queryClient.invalidateQueries(['occasion']);
          if (refreshData) {
            onSuccess();
            return;
          }
        }
      } catch (e: any) {}
    }
    const refreshData = await updateData(cafeteriaId);
    if (refreshData) {
      onSuccess();
    }
  };

  const handleFormSubmit = async (values: SelectCafeteriaValues) => {
    if (
      cartData?.cart_data &&
      Number(values?.cafeteria) !== Number(cartData?.cart_data?.location_id)
    ) {
      if (redirectCart) {
        setIsModalOpen(true);
        return false;
      }
      clearCart(values);
    } else {
      if (redirectCart) {
        onSuccess('/cart');
        return false;
      }
      void handleCityChange(values);
    }
  };

  return (
    <>
      <form
        className="full-width"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.onSubmit((values) => {
          void handleFormSubmit(values);
        })}
      >
        <Flex direction="column" mb={{ base: 'sm', sm: 'lg' }}>
          <Text fw={600} component="p" mb="2px">
            {groupLocation ? 'Select your city & cafeteria:' : 'Select your cafeteria:'}
          </Text>
          {cartData?.cart_data?.cart_items
            ? Object.keys(cartData?.cart_data?.cart_items).length > 0 && (
                <Flex align="center">
                  <img
                    src={`${ASSETS_BUCKET_URL}/svg/Warning.svg`}
                    alt="Warning"
                    loading="lazy"
                    className="sm-icon"
                  />
                  <Text c="dark" fz={{ base: 'sm', sm: 'md' }} lh={1.2} fw={400}>
                    Changing the cafeteria will clear your cart.
                  </Text>
                </Flex>
              )
            : null}
        </Flex>

        {groupLocation && (
          <SelectField
            label="City"
            data={cities}
            register={{ ...form.getInputProps('city') }}
            mb={{ base: 'sm', sm: 'md' }}
            dropdownPosition={!isMobile ? 'bottom' : 'top'}
          />
        )}
        <SelectField
          label="Cafeteria"
          data={cafeteria}
          register={{ ...form.getInputProps('cafeteria') }}
          mb="xl"
        />

        {webAccessToken && (
          <Flex justify="flex-start" align="flex-start" direction="row" my="lg">
            <CheckboxFormField mr="xs" register={{ ...form.getInputProps('defaultCafeteria') }} />
            <Text c="dark" span className="pointer">
              Set this location as your default cafeteria.
            </Text>
          </Flex>
        )}

        <div className="auth-buttons-spacing">
          <Button type="submit" fullWidth className={!form.isValid() ? 'disabled-button' : ''}>
            Proceed
          </Button>
        </div>
      </form>
      <ActionPopUpModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        displayContent="Changing the cafeteria will clear your cart!"
        showCloseIcon={false}
        acceptTitle="Continue"
        onAccept={() => {
          clearCart(form.values);
        }}
        rejectTitle="Cancel"
        onReject={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};
